import { Link, usePage } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, Users, Plus } from "lucide-react";
import { EventBadge } from "@/components/app/EventBadge";
import Avatar from "@/components/app/Avatar";

export default function EventsIndex() {
  const { routes, auth, events } = usePage().props;

  return (
    <AppLayout>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Upcoming events</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Public game nights open to everyone</p>
          </div>
          {auth.authenticated && (
            <Button asChild>
              <Link href={routes.new_event}>
                <Plus className="h-4 w-4" />
                Host an event
              </Link>
            </Button>
          )}
        </div>

        {events.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No upcoming events</p>
            <p className="text-sm mt-1">Be the first to host a game night!</p>
            {auth.authenticated && (
              <Button asChild className="mt-4">
                <Link href={routes.new_event}>Create event</Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function EventCard({ event }) {
  const startsAt = event.starts_at ? new Date(event.starts_at) : null;

  return (
    <Link href={event.paths.show} className="block group">
      <div className="rounded-lg border border-border bg-card p-5 shadow-sm hover:border-primary/40 hover:shadow-md transition-all space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="font-semibold text-base leading-tight group-hover:text-primary transition-colors line-clamp-2">
            {event.title}
          </h2>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {event.status === "cancelled" && (
              <Badge variant="destructive">Cancelled</Badge>
            )}
            <EventBadge relationship={event.relationship} rsvpStatus={event.rsvp_status} />
          </div>
        </div>

        {event.description && (
          <p className="text-muted-foreground text-sm line-clamp-2 leading-relaxed">
            {event.description}
          </p>
        )}

        <div className="space-y-1.5 text-sm text-muted-foreground">
          {startsAt && (
            <div className="flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5 shrink-0" />
              <span>
                {startsAt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })},{" "}
                {startsAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
          )}
          {event.location_name && (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{event.location_name}</span>
            </div>
          )}
          {event.player_limit && (
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span>Up to {event.player_limit} players</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Avatar name={event.host.full_name} url={event.host.avatar_url} size="xs" />
          <Link
            href={event.host.path}
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {event.host.full_name}
          </Link>
          {!event.accepting_rsvps && event.status !== "cancelled" && (
            <Badge variant="secondary" className="ml-auto text-xs">Full</Badge>
          )}
        </div>
      </div>
    </Link>
  );
}

